/**
 * Sysadmin Catalog → Agents hooks (PR-K2).
 *
 * Wraps `/api/v1/admin/platform/catalog/agents/*` — the LAST and
 * HEAVIEST catalog leaf because Agents COMPOSE every other entity
 * (model + prompt + tools + functions + skills + library + workflows).
 *
 * Mirrors the pattern in `use-sysadmin-catalog-skills.ts` with
 * lifecycle helpers, clones list, version history, and test-bench.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Types ────────────────────────────────────────────────────────────────

export type AgentStatus = 'draft' | 'published' | 'deprecated' | 'retired';

export type LibraryRefKind =
  | 'folder'
  | 'article'
  | 'tag'
  | 'document'
  | 'document_folder'
  | 'source';

export interface LibraryRef {
  kind: LibraryRefKind;
  id: string;
}

export interface AgentBehaviorConfig {
  autonomyLevel?: 'read_only' | 'suggest' | 'auto_approve';
  approvalPolicy?: 'auto_approve' | 'require_approval' | 'forbidden';
  maxSteps?: number;
  displayTier?: 'minimal' | 'standard' | 'detailed';
  safetyVerificationGate?: boolean;
  [key: string]: unknown;
}

export interface AgentTriggers {
  conversational?: boolean;
  scheduled?: { cron: string; tz?: string } | null;
  event?: { types: string[] } | null;
  [key: string]: unknown;
}

export interface CatalogAgent {
  id: string;
  slug: string;
  displayName: string;
  description: string;
  category: string;
  modelBrand: string;
  modelId: string | null;
  /**
   * Multi-model selection (phase4/catalog-reference-picker — founder
   * bug C1, UI sweep 2026-05-11). Derived from `bases` jsonb via the
   * post-bU8d system_agents view. The first entry is the legacy primary
   * `modelId`. Empty array when no model is assigned.
   */
  modelIds: string[];
  systemPromptId: string | null;
  systemPrompt: string;
  personaPrompt: string | null;
  defaultLocale: string;
  auditCategory: string;
  toolSlugs: string[];
  functionKeys: string[];
  skillSlugs: string[];
  libraryRefs: LibraryRef[];
  workflowSlugs: string[];
  behaviorConfig: AgentBehaviorConfig;
  triggers: AgentTriggers;
  kind: string | null;
  version: number;
  isActive: boolean;
  status: AgentStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  unpublishedAt: string | null;
  unpublishedBy: string | null;
  unpublishReason: string | null;
  cloneCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AgentCompositionRow {
  id: string;
  publicId: string;
  displayName: string;
  status: string;
  isActive: boolean;
}

export interface AgentLibraryRefResolved {
  kind: LibraryRefKind;
  id: string;
  displayName: string;
  status: string;
}

export interface AgentVersionEntry {
  id: string;
  version: number;
  changedAt: string;
  changedByAdmin: string | null;
}

export interface CatalogAgentDetail extends CatalogAgent {
  composition: {
    model: {
      id: string;
      brandId: string;
      displayName: string;
      isActive: boolean;
    } | null;
    systemPromptTemplate: {
      id: string;
      slug: string;
      displayName: string;
      isActive: boolean;
    } | null;
    tools: AgentCompositionRow[];
    functions: AgentCompositionRow[];
    skills: AgentCompositionRow[];
    libraryRefs: AgentLibraryRefResolved[];
    workflows: AgentCompositionRow[];
    danglingToolSlugs: string[];
    danglingFunctionKeys: string[];
    danglingSkillSlugs: string[];
    danglingLibraryRefs: LibraryRef[];
    danglingWorkflowSlugs: string[];
    workflowsValidationDeferred: boolean;
  };
  versions: AgentVersionEntry[];
}

export interface CatalogAgentClone {
  id: string;
  tenantId: string;
  tenantName: string | null;
  sourceAgentId: string;
  sourceVersion: number;
  tenantAgentId: string;
  tenantAgentName: string | null;
  clonedBy: string | null;
  clonedAt: string;
  syncStatus: string;
  notes: string | null;
}

export interface CatalogAgentListResponse {
  items: CatalogAgent[];
  nextCursor: string | null;
}

export interface TestBenchResult {
  invocationId: string;
  agentId: string;
  agentSlug: string;
  status: 'stub_completed';
  warnings: string[];
  response: string;
  tokens: { input: number; output: number };
  latencyMs: number;
}

const BASE = '/admin/platform';

const KEYS = {
  list: (filters: Record<string, unknown>) =>
    ['sysadmin-catalog-agents', 'list', filters] as const,
  detail: (id: string) =>
    ['sysadmin-catalog-agents', 'detail', id] as const,
  clones: (id: string) =>
    ['sysadmin-catalog-agents', 'clones', id] as const,
  versions: (id: string) =>
    ['sysadmin-catalog-agents', 'versions', id] as const,
};

// ─── List + detail ────────────────────────────────────────────────────────

export interface UseCatalogAgentsParams {
  status?: AgentStatus;
  category?: string;
  search?: string;
  trigger?: 'conversational' | 'scheduled' | 'event';
  limit?: number;
}

export function useCatalogAgents(params: UseCatalogAgentsParams = {}) {
  return useInfiniteQuery<CatalogAgentListResponse, Error>({
    queryKey: KEYS.list(params as Record<string, unknown>),
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.category) qs.set('category', params.category);
      if (params.search) qs.set('search', params.search);
      if (params.trigger) qs.set('trigger', params.trigger);
      if (params.limit) qs.set('limit', String(params.limit));
      if (pageParam) qs.set('cursor', String(pageParam));
      const query = qs.toString() ? `?${qs.toString()}` : '';
      return apiClient.get<CatalogAgentListResponse>(
        `${BASE}/catalog/agents${query}`,
      );
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

export function useCatalogAgent(id: string | null | undefined) {
  return useQuery<{ data: CatalogAgentDetail }, Error>({
    queryKey: id ? KEYS.detail(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ data: CatalogAgentDetail }>(
        `${BASE}/catalog/agents/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCatalogAgentClones(id: string | null | undefined) {
  return useQuery<{ items: CatalogAgentClone[] }, Error>({
    queryKey: id ? KEYS.clones(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get<{ items: CatalogAgentClone[] }>(
        `${BASE}/catalog/agents/${id}/clones`,
      ),
    enabled: !!id,
  });
}

export function useCatalogAgentVersions(id: string | null | undefined) {
  return useQuery<
    {
      items: Array<{
        id: string;
        version: number;
        changedAt: string;
        changedByAdmin: string | null;
        snapshot: Record<string, unknown>;
      }>;
    },
    Error
  >({
    queryKey: id ? KEYS.versions(id) : ['__skip__'],
    queryFn: () =>
      apiClient.get(`${BASE}/catalog/agents/${id}/versions`),
    enabled: !!id,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────

export interface CreateCatalogAgentInput {
  slug: string;
  displayName: string;
  description: string;
  category?: string;
  modelBrand?: string;
  modelId?: string | null;
  /** Multi-model — see CatalogAgent.modelIds. Optional; first entry is
   * persisted to legacy modelId for back-compat. */
  modelIds?: string[];
  systemPromptId?: string | null;
  systemPrompt: string;
  personaPrompt?: string | null;
  defaultLocale?: string;
  auditCategory?: string;
  toolSlugs?: string[];
  functionKeys?: string[];
  skillSlugs?: string[];
  libraryRefs?: LibraryRef[];
  workflowSlugs?: string[];
  behaviorConfig?: AgentBehaviorConfig;
  triggers?: AgentTriggers;
  kind?: string | null;
}

export function useCreateCatalogAgent() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogAgent }, Error, CreateCatalogAgentInput>({
    mutationFn: (body) =>
      apiClient.post<{ data: CatalogAgent }>(
        `${BASE}/catalog/agents`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-agents'] });
    },
  });
}

export interface UpdateCatalogAgentInput
  extends Partial<Omit<CreateCatalogAgentInput, 'slug'>> {
  id: string;
}

export function useUpdateCatalogAgent() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogAgent }, Error, UpdateCatalogAgentInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: CatalogAgent }>(
        `${BASE}/catalog/agents/${id}`,
        body,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-agents'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteCatalogAgent() {
  const qc = useQueryClient();
  return useMutation<{ success: true }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: true }>(`${BASE}/catalog/agents/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-agents'] });
    },
  });
}

export function usePublishCatalogAgent() {
  const qc = useQueryClient();
  return useMutation<{ data: CatalogAgent }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.post<{ data: CatalogAgent }>(
        `${BASE}/catalog/agents/${id}/publish`,
        {},
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-agents'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export interface UnpublishCatalogAgentInput {
  id: string;
  reason: string;
  nextStatus?: 'draft' | 'deprecated' | 'retired';
}

export function useUnpublishCatalogAgent() {
  const qc = useQueryClient();
  return useMutation<
    { data: CatalogAgent },
    Error,
    UnpublishCatalogAgentInput
  >({
    mutationFn: ({ id, reason, nextStatus }) =>
      apiClient.post<{ data: CatalogAgent }>(
        `${BASE}/catalog/agents/${id}/unpublish`,
        { reason, nextStatus: nextStatus ?? 'deprecated' },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['sysadmin-catalog-agents'] });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export interface TestBenchInput {
  id: string;
  query: string;
  variables?: Record<string, string>;
}

export function useTestBenchCatalogAgent() {
  return useMutation<TestBenchResult, Error, TestBenchInput>({
    mutationFn: ({ id, query, variables }) =>
      apiClient.post<TestBenchResult>(
        `${BASE}/catalog/agents/${id}/test-bench`,
        { query, variables },
      ),
  });
}
